import cln from 'classnames';

import { useLocation, useNavigate } from 'react-router';
import { PromotionAdaptedEntity } from 'react-memory-optimization/dist/lib/store/casino/entities/promotions/types';
import {
  DrawerButton,
  DrawerLayout,
  ProfileSvg,
  FavoritesSvg,
  VipClubSvg,
  ProvidersSvg,
  NavTournamentSvg,
  // MobileApp,
  NavReferralSvg,
  DownloadSvg,
  useZustandModalKitStore,
  EModalTypes,
  zustandModalKitSelector,
  sleep,
  Text,
  NavCasinoSvg,
  NavBettingSvg,
  LotterySvg,
} from 'react-gldn-kit';
import { useFavoriteGames, useAuth, usePromotions } from 'react-render-hooks';
import { AdaptedGame } from 'react-memory-optimization/dist/lib/store/casino/entities/games/types';
import { AuthInfo } from 'react-memory-optimization/dist/lib/store/user/types';

import PromotionBanner from './components/PromotionBanner';
import CasinoProviderButtons from './components/CasinoProviderButtons';
import BonusButtons from './components/BonusButtons';
import SocialButtons from './components/SocialButtons';
import ButtonLanguage from 'components/ui/Buttons/ButtonLanguage';
import JivoChat from 'components/JivoChat';
import ComponentKitLogo from 'components/ui/KitComponents/ComponentKitLogo';

import { BASE_URL } from 'constants/transport';

import { useHookFabric } from 'hooks/useHookFabric';

import {
  getDrawerStateSelector,
  isFullCasinoDrawerSelector,
  setDrawerStateAction,
} from 'stores/drawers/casino/selectors';
import { useZustandCasinoDrawerStore } from 'stores/drawers/casino/store';

import { isMobileDeviceSelector } from 'stores/device/selectors';
import { useZustandDeviceStore } from 'stores/device/store';

import { ERouteNames } from 'Routes/types';

import styles from './CasinoDrawer.module.css';

type Props = {
  closeCallback?: (e?: React.MouseEvent<HTMLDivElement>) => void;
};

const CasinoDrawer = ({ closeCallback }: Props) => {
  const { pathname } = useLocation();

  const isClosed = useZustandCasinoDrawerStore(getDrawerStateSelector);

  const setCloseState = useZustandCasinoDrawerStore(setDrawerStateAction);

  const isMobile = useZustandDeviceStore(isMobileDeviceSelector);

  const isAuth = useHookFabric<AuthInfo>({
    fn: useAuth,
  });

  const promotion = useHookFabric<PromotionAdaptedEntity>({
    fn: usePromotions,
    customProps: {
      isActive: true,
    },
  });

  const closeAndOpen = useZustandModalKitStore(
    zustandModalKitSelector.closeAndOpenModalAction
  );

  const isFullView = useZustandCasinoDrawerStore(isFullCasinoDrawerSelector);

  const games = useHookFabric<AdaptedGame[]>({
    fn: useFavoriteGames,
  });

  const navigate = useNavigate();

  const toggleCloseState = async (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (!isMobile) {
      return;
    }
    setCloseState(!isClosed);
    await sleep(300);
    // Need to prevent animation bag
    setCloseState(false);
    closeCallback?.(e);
  };

  const jumpTo =
    (path: string, isAuthImportant = false) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      toggleCloseState(e);

      if (!isAuth && isAuthImportant) {
        closeAndOpen(EModalTypes.SignIn, {
          title: 'authorization.title',
        });

        return;
      }

      navigate(path);
    };

  const openModal =
    (
      t: (typeof EModalTypes)[keyof typeof EModalTypes],
      isAuthImportant = false
    ) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      toggleCloseState(e);

      if (isAuthImportant && !isAuth) {
        closeAndOpen(EModalTypes.SignIn);

        return;
      }
      closeAndOpen(t);
    };

  return (
    <DrawerLayout
      isMobile={isMobile}
      isFullView={isFullView}
      isCloseDrawer={isClosed}
      closeCallback={toggleCloseState}
      classes={cln({
        [styles.short]: !isFullView,
      })}
    >
      <div>
        {!isMobile && (
          <div className={styles.desktopLogo}>
            <ComponentKitLogo />
          </div>
        )}

        {promotion?.assets.drawer && (
          <PromotionBanner
            handleClose={toggleCloseState}
            bgPath={promotion.assets.drawer.path}
            translate={promotion.drawerTranslates}
          />
        )}

        <div
          className={cln(styles.drawerButtons, {
            [styles.withoutPromoBanner]: !promotion,
          })}
        >
          <DrawerButton
            size="full"
            text="navigationBar.casino"
            handleClick={jumpTo(ERouteNames.Home)}
            startIcon={
              <NavCasinoSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln(styles.desktopOnly, {
              [styles.active]: pathname === ERouteNames.Home,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="navigationBar.betting"
            handleClick={jumpTo(ERouteNames.FrameBetting)}
            startIcon={
              <NavBettingSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln(styles.desktopOnly, {
              [styles.active]: pathname === ERouteNames.FrameBetting,
            })}
            centreText={!isFullView}
            endIconClasses={styles.sportChild}
          >
            <div className={styles.sportNew}>
              <div>
                <Text idT="drawers.new" />
              </div>
              <img
                src={`${BASE_URL}/icons-elon/drawer/ball.png`}
                className={styles.ball}
              />
            </div>
          </DrawerButton>

          <DrawerButton
            size="full"
            text="profile.tabs.myAccount"
            handleClick={jumpTo(ERouteNames.ProfileAccount, true)}
            startIcon={
              <ProfileSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.ProfileAccount,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="favorites"
            handleClick={openModal(EModalTypes.CasinoFavorite, true)}
            startIcon={
              <FavoritesSvg
                classes={cln(styles.stroke, styles.icon, {
                  [styles.hasFavItems]: games?.length,
                })}
              />
            }
            isFullView={isFullView}
            centreText={!isFullView}
          >
            {games?.length ? (
              <div className={styles.favItems}>{games?.length}</div>
            ) : undefined}
          </DrawerButton>

          <BonusButtons closeCallback={closeCallback} />

          <DrawerButton
            size="full"
            text="vipClub.vipClub"
            handleClick={jumpTo(ERouteNames.PromotionsVipClub)}
            startIcon={
              <VipClubSvg classes={cln(styles.vipClubSvg, styles.icon)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.PromotionsVipClub,
            })}
            centreText={!isFullView}
          />

          <CasinoProviderButtons />

          <DrawerButton
            size="full"
            text="battles"
            handleClick={jumpTo(ERouteNames.Battles)}
            startIcon={
              <NavTournamentSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.Battles,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="navigationBar.promoCode"
            handleClick={jumpTo(ERouteNames.PromoCode, true)}
            startIcon={
              <LotterySvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.PromoCode,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="referral.button"
            handleClick={jumpTo(ERouteNames.ReferralProgram, true)}
            startIcon={
              <NavReferralSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.ReferralProgram,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="offers"
            handleClick={jumpTo(ERouteNames.Promotions)}
            startIcon={
              <ProvidersSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.Promotions,
            })}
            centreText={!isFullView}
          />

          <DrawerButton
            size="full"
            text="mobileApp"
            handleClick={jumpTo(ERouteNames.Applications)}
            startIcon={
              <DownloadSvg classes={cln(styles.icon, styles.colorful)} />
            }
            isFullView={isFullView}
            classes={cln({
              [styles.active]: pathname === ERouteNames.Applications,
            })}
            centreText={!isFullView}
          />
        </div>
      </div>

      <div className={styles.dash} />

      <div className={styles.wrapperLangAndSupport}>
        <ButtonLanguage
          isWithName={isFullView}
          classes={styles.buttonLang}
          isWithArrow
        />

        <JivoChat
          classes={cln(styles.jivo, styles.desktopOnly)}
          isDisableText={!isFullView}
        />
      </div>

      <div className={styles.social}>
        {isFullView && (
          <div className={styles.socialTitle}>
            <Text idT="bigGive.social.title" />
          </div>
        )}

        <SocialButtons />
      </div>
    </DrawerLayout>
  );
};

export default CasinoDrawer;
